import { useEffect, useMemo, useRef, useState } from 'react'
import {
  ETypeOfNotification,
  Modal,
  RColumn,
  RemoteTable,
  RemoteTableRef,
  setNotification,
  stringFilterTemplate,
} from 'medere-ui-framework'
import { Button } from 'primereact/button'
import { MultiSelect, MultiSelectChangeEvent } from 'primereact/multiselect'
import { useDispatch } from 'react-redux'
import { UsersListItemDTO, UsersListQueryParams } from '../../dto/users/UsersDTO'
import {
  deleteUser,
  getUserProfiles,
  getUsersList,
  markUserInactive,
} from '../../servicesHooks/Users/useUsersService'
import LocationActionMenu, { LocationActionItem } from '../Location/tabs/components/LocationActionMenu'
import UsersListCard, { UsersCardActionsProps } from './components/UsersListCard'
import UsersFormView from './UsersFormView'
import './Users.scss'

type UnknownRecord = Record<string, unknown>

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null
}

interface FilterOption<TValue> {
  label: string
  value: TValue
}

const MOBILE_SEARCH_BATCH_SIZE = 200
const MOBILE_SEARCH_MAX_SCAN = 2000
const MOBILE_FILTER_DEBOUNCE_MS = 300

function normalizeFilterText(value: string | undefined | null) {
  return String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
}

function matchesMobileFilter(item: UsersListItemDTO, normalizedFilterText: string) {
  if (normalizedFilterText === '') {
    return true
  }

  const searchableValues = [
    item.firstname,
    item.lastname,
    item.username,
    item.profile,
    item.document,
    item.blocked ? 'bloqueado' : 'activo',
  ]

  return searchableValues.some((value) => normalizeFilterText(value).includes(normalizedFilterText))
}

export default function UsersView() {
  const dispatch = useDispatch()
  const tableRef = useRef<RemoteTableRef>(null)
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null)
  const [selectedMode, setSelectedMode] = useState<'edit' | 'view'>('edit')
  const [profileFilter, setProfileFilter] = useState<string[]>([])
  const [blockedFilter, setBlockedFilter] = useState<boolean[]>([])
  const [mobileFilterDraft, setMobileFilterDraft] = useState('')
  const [mobileFilterText, setMobileFilterText] = useState('')
  const [profileOptions, setProfileOptions] = useState<Array<FilterOption<string>>>([])
  const [deleteModal, setDeleteModal] = useState(false)
  const [userToDelete, setUserToDelete] = useState<UsersListItemDTO | null>(null)
  const scope: 'admin' | 'auditor' = 'admin'
  const title = 'Usuarios'

  const blockedOptions: Array<FilterOption<boolean>> = [
    { label: 'Bloqueado', value: true },
    { label: 'Activo', value: false },
  ]

  function clearTableFiltersStorage() {
    const tableStateKeys = ['UsersTable', 'mobileFilter_UsersTable']
    tableStateKeys.forEach((key) => {
      localStorage.removeItem(key)
      sessionStorage.removeItem(key)
    })
  }

  useEffect(() => {
    clearTableFiltersStorage()
    void getUserProfiles({ filterValue: '', offSet: 0, pageSize: 200 })
      .then((items) => {
        setProfileOptions(
          items
            .map((item) => ({ label: item.name ?? '', value: item.name ?? '' }))
            .filter((item) => item.value !== ''),
        )
      })
      .catch((error) => {
        console.error(error)
      })

    return () => {
      clearTableFiltersStorage()
    }
  }, [])

  useEffect(() => {
    tableRef.current?.refreshTable?.()
  }, [blockedFilter, mobileFilterText, profileFilter])

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setMobileFilterText(mobileFilterDraft)
    }, MOBILE_FILTER_DEBOUNCE_MS)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [mobileFilterDraft])

  async function loadUsers(offSet: number, pageSize: number, params: UsersListQueryParams = {}) {
    const normalizedMobileFilterText = normalizeFilterText(mobileFilterText)

    if (normalizedMobileFilterText === '') {
      return getUsersList(offSet, pageSize, {
        ...params,
        scope,
        profileFilter,
        blockedFilter: blockedFilter.length > 0 ? blockedFilter : [],
      })
    }

    const fetchSize = Math.max(MOBILE_SEARCH_BATCH_SIZE, pageSize)
    let fetchOffset = 0
    let scannedItems = 0
    const requiredMatches = offSet + pageSize
    const filteredItems: UsersListItemDTO[] = []

    while (scannedItems < MOBILE_SEARCH_MAX_SCAN && filteredItems.length < requiredMatches) {
      const batch = await getUsersList(fetchOffset, fetchSize, {
        ...params,
        scope,
        profileFilter,
        blockedFilter: blockedFilter.length > 0 ? blockedFilter : [],
        cardFilterDTO: null,
      })

      if (batch.length === 0) {
        break
      }

      scannedItems += batch.length
      filteredItems.push(...batch.filter((item) => matchesMobileFilter(item, normalizedMobileFilterText)))

      if (batch.length < fetchSize) {
        break
      }

      fetchOffset += fetchSize
    }

    return filteredItems.slice(offSet, offSet + pageSize)
  }

  function openView(item: UsersListItemDTO) {
    setSelectedMode('view')
    setSelectedUserId(item.userId)
  }

  function openEdit(item: UsersListItemDTO) {
    setSelectedMode('edit')
    setSelectedUserId(item.userId)
  }

  function openDelete(item: UsersListItemDTO) {
    setUserToDelete(item)
    setDeleteModal(true)
  }

  async function confirmDelete() {
    if (!userToDelete) {
      setDeleteModal(false)
      return
    }

    try {
      await deleteUser(userToDelete.userId)
      dispatch(setNotification({
        title: 'Usuario eliminado',
        desc: 'El usuario se eliminó correctamente.',
        typeOfNotification: ETypeOfNotification.INFO,
        icon: 'pi pi-check',
      }))
      tableRef.current?.refreshTable?.()
    } catch (error: unknown) {
      try {
        await markUserInactive(userToDelete.userId)
        dispatch(setNotification({
          title: 'Usuario pasado a inactivo',
          desc: 'No se pudo eliminar el usuario, por lo que se lo marcó como bloqueado.',
          typeOfNotification: ETypeOfNotification.WARNING,
          icon: 'pi pi-exclamation-triangle',
        }))
        tableRef.current?.refreshTable?.()
      } catch (inactiveError: unknown) {
        const response = isRecord(inactiveError) && isRecord(inactiveError.response) ? inactiveError.response : null
        const status = typeof response?.status === 'number' ? response.status : null

        if (status === 409) {
          dispatch(setNotification({
            title: 'No se puede eliminar',
            desc: 'El usuario está siendo utilizado en otras relaciones.',
            typeOfNotification: ETypeOfNotification.WARNING,
            icon: 'pi pi-exclamation-triangle',
          }))
        } else {
        dispatch(setNotification({
          title: 'Error',
          desc: 'No se pudo eliminar ni pasar a inactivo el usuario.',
          typeOfNotification: ETypeOfNotification.ERROR,
          icon: 'pi pi-times',
        }))
        }
      }
    } finally {
      setDeleteModal(false)
      setUserToDelete(null)
    }
  }

  function getFilterText(input: unknown): string {
    if (typeof input === 'string') return input
    if (!isRecord(input)) return ''
    const target = isRecord(input.target) ? input.target : null
    return typeof target?.value === 'string' ? target.value : ''
  }

  function handleMobileFilterChange(input: unknown) {
    setMobileFilterDraft(getFilterText(input))
  }

  const handleClearFilters = useMemo(() => () => {
    setProfileFilter([])
    setBlockedFilter([])
  }, [])

  function handleTableClearFiltersClick(event: React.MouseEvent<HTMLDivElement>) {
    const target = event.target
    if (!(target instanceof HTMLElement)) {
      return
    }

    const clearButton = target.closest('button[aria-label="Limpiar filtros"]')
    if (!clearButton) {
      return
    }

    clearTableFiltersStorage()
    window.setTimeout(() => {
      handleClearFilters()
    }, 0)
  }

  const cardActionsProps = useMemo<UsersCardActionsProps>(() => ({
    onView: openView,
    onEdit: openEdit,
    onDelete: openDelete,
  }), [])

  const tableParams = useMemo(() => ({
    scope,
    profileFilter,
    blockedFilter: blockedFilter.length > 0 ? blockedFilter : [],
  }), [blockedFilter, profileFilter, scope])

  const tableRenderKey = useMemo(
    () => `users-table-${profileOptions.map((option) => option.value).join('|')}--${profileFilter.join('|')}--${blockedFilter.join('|')}`,
    [blockedFilter, profileFilter, profileOptions],
  )

  const rowActions = useMemo(() => (rowData: UsersListItemDTO) => {
    const menuItems: LocationActionItem[] = [
      {
        label: 'Ver detalle',
        icon: 'pi pi-eye',
        action: () => openView(rowData),
      },
      {
        label: 'Editar',
        icon: 'pi pi-file-edit',
        action: () => openEdit(rowData),
      },
      {
        label: 'Eliminar',
        icon: 'pi pi-trash',
        action: () => openDelete(rowData),
      },
    ]

    return (
      <LocationActionMenu
        items={menuItems}
        ariaLabel='Opciones del usuario'
        buttonId={`usersMenu_${rowData.userId}`}
        buttonClassName='p-button-text'
        panelClassName='users-entities-action-menu-panel'
      />
    )
  }, [])

  const profileFilterElement = useMemo(() => () => (
    <MultiSelect
      options={profileOptions}
      optionLabel='label'
      optionValue='value'
      value={profileFilter}
      onChange={(e: MultiSelectChangeEvent) => setProfileFilter((e.value ?? []) as string[])}
      className='w-full users-entities-filter-multiselect'
      style={{ height: '2.25rem', marginBottom: '0.28rem' }}
    />
  ), [profileFilter, profileOptions])

  const blockedFilterElement = useMemo(() => () => (
    <MultiSelect
      options={blockedOptions}
      optionLabel='label'
      optionValue='value'
      value={blockedFilter}
      onChange={(e: MultiSelectChangeEvent) => setBlockedFilter((e.value ?? []) as boolean[])}
      className='w-full users-entities-filter-multiselect'
      style={{ height: '2.25rem', marginBottom: '0.28rem' }}
    />
  ), [blockedFilter])

  if (selectedUserId != null) {
    return (
      <UsersFormView
        userId={selectedUserId}
        mode={selectedMode}
        setUserId={setSelectedUserId}
        setMode={setSelectedMode}
        onSaved={() => tableRef.current?.refreshTable?.()}
      />
    )
  }

  return (
    <div className='viewContainer users-entities-view' onClick={handleTableClearFiltersClick}>
      <div className='users-entities-header'>
        <h1 className='users-entities-title'>{title}</h1>
        <Button
          id='buttonCreateUsersId'
          type='button'
          className='buttonMain'
          label='Crear'
          icon='pi pi-plus-circle'
          onClick={() => {
            setSelectedMode('edit')
            setSelectedUserId(-1)
          }}
        />
      </div>
      <div className='users-entities-divider' />

      <RemoteTable
        key={tableRenderKey}
        idTable='UsersTable'
        dataKey='userId'
        className='users-entities-table'
        getItems={loadUsers}
        resizableColumns
        dynamicBehaviour
        CardTemplate={UsersListCard}
        cardKeyName='userId'
        cardAdditionalProps={cardActionsProps}
        handleMobileFilter={handleMobileFilterChange}
        params={tableParams}
        ref={tableRef}
        filterRow
        rowActions={rowActions}
      >
        <RColumn field='username' className='column' header='Usuario' filter filterElement={stringFilterTemplate} />
        <RColumn field='firstname' className='column' header='Nombre' filter filterElement={stringFilterTemplate} />
        <RColumn field='lastname' className='column' header='Apellido' filter filterElement={stringFilterTemplate} />
        <RColumn field='profile' className='column' header='Perfil' filter filterElement={profileFilterElement} />
        <RColumn field='document' className='column' header='N° de Documento' filter filterElement={stringFilterTemplate} />
        <RColumn
          field='blocked'
          className='column'
          header='Estado'
          body={(item: UsersListItemDTO) => (item.blocked ? 'Bloqueado' : 'Activo')}
          filter
          filterElement={blockedFilterElement}
        />
      </RemoteTable>

      <Modal
        visible={deleteModal}
        setVisible={setDeleteModal}
        header='Eliminar usuario'
        footerButtonRightText='Eliminar'
        footerButtonLeftText='Cancelar'
        onClickRightBtn={() => { void confirmDelete() }}
        onClickLeftBtn={() => setDeleteModal(false)}
      >
        <p>
          ¿Querés eliminar al usuario <strong>{userToDelete?.username ?? ''}</strong>?
        </p>
      </Modal>
    </div>
  )
}
